<template>
  <div>
    <page-content-toolbar
      :projectName="`${project && project.name ? project.name : ' '}`"
      :title="$t('scenario.list')"
    />

    <div class="mx-8 mt-6">
      <v-data-table
        :loading="isLoading"
        :headers="optionHeaders"
        :items="optionsOnly"
        :server-items-length="optionsOnly.length"
        hide-default-footer
        class="elevation-1 rai-projects-table"
        item-key="id"
      >
        <template v-slot:item="{ item, expand, isExpanded }" :item-key="item.name">
          <tr :item-key="item.id">
            <td class="rai-table__column">
              <option-chip
                :option="item"
                :close="true"
                :close-function="deleteOption"
                :edit="true"
                :edit-function="editOption"
                :edit-function-parameters="[item]"
                :close-function-parameters="[item]"
              />
            </td>
             
            <td class="rai-table__column">
              <v-btn
                class="elevation-0 ma-auto"
                :color="item.scenarios ? 'green' : 'red'"
                fab
                small
              >
                <v-icon dark>mdi-{{item.scenarios ? 'check' : 'close' }}</v-icon>
              </v-btn>
            </td>
            <td class="rai-table__column">
              <option-chip
                v-for="conflictingOption in item.conflictingOptions"
                :key="conflictingOption.id"
                :option="conflictingOption"
                :close="false"
                :edit-function-parameters="[conflictingOption]"
                :close-function-parameters="[conflictingOption]"
              />
            </td>
            <td class="rai-table__column">
              <v-btn
                @click="expand(!isExpanded)"
                class="elevation-0 ma-auto"
                color="ternary"
                fab
                icon
                small
              >
                <v-icon large>mdi-chevron-{{ isExpanded ? 'up' : 'down' }}</v-icon>
              </v-btn>
            </td>
          </tr>
        </template>
        <template v-slot:expanded-item="{ item }">
          <tr>
            <td colspan="6" style="margin-top:300px;">
              <span v-show="!item.scenarios">
                {{ $t('scenario.noScenario') }}
              </span>
              <br/>
              <scenario-block
                v-for="scenario in item.scenarios"
                :key="scenario.id"
                :displayHeader=true
                :option="item"
                :scenario="scenario"
                :delete-element=false
                :delete-element-function="deleteElement"
                :edit-element=true
                :edit-element-function="editElement"
                :allow-scenario-edition=false
                :available-elements="availableElements"
                :update-element-list="replaceElementEveryWhere"
                :update-available-scenarios="replaceScenarioEveryWhere"
                :allow-incompatible="scenario.level < 2"
                :enable-resolved-check-mark="false"
                v-on="$listeners"
              />
            </td>
          </tr>
        </template>
      </v-data-table>

      <v-data-table
        :loading="isLoading"
        :headers="beliefHeaders"
        :items="beliefsOnly"
        :server-items-length="beliefsOnly.length"
        hide-default-footer
        class="elevation-1 rai-projects-table  mt-6"
        item-key="id"
      >
        <template v-slot:item="{ item, expand, isExpanded }" :item-key="item.name">
          <tr :item-key="item.id">
             <td class="rai-table__column">
              <element-chip
                :element="item"
                :edit="true"
                :edit-function="editElement"
                :edit-function-parameters="[item]"
              />
            </td>
            <td class="rai-table__column">
              <v-btn
                class="elevation-0 ma-auto"
                :color="item.scenarios ? 'green' : 'red'"
                fab
                small
              >
                <v-icon dark>mdi-{{item.scenarios ? 'check' : 'close' }}</v-icon>
              </v-btn>
            </td>
            <td class="rai-table__column">
              <option-chip
                v-for="conflictingOption in item.conflictingOptions"
                :key="conflictingOption.id"
                :option="conflictingOption"
                :close="false"
                :edit-function-parameters="[conflictingOption]"
                :close-function-parameters="[conflictingOption]"
              />
            </td>
            <td class="rai-table__column">
              <v-btn
                @click="expand(!isExpanded)"
                class="elevation-0 ma-auto"
                color="ternary"
                fab
                icon
                small
              >
                <v-icon large>mdi-chevron-{{ isExpanded ? 'up' : 'down' }}</v-icon>
              </v-btn>
            </td>
          </tr>
        </template>
        <template v-slot:expanded-item="{ item }">
          <tr>
            <td colspan="6" style="margin-top:300px;">
              <span v-show="!item.scenarios">
                {{ $t('scenario.noScenario') }}
              </span>
              <br/>
              <scenario-block
                v-for="scenario in item.scenarios"
                :key="scenario.id"
                :displayHeader=true
                :option="item"
                :scenario="scenario"
                :delete-element=false
                :delete-element-function="deleteElement"
                :edit-element=true
                :edit-element-function="editElement"
                :allow-scenario-edition=false
                :available-elements="availableElements"
                :update-element-list="replaceElementEveryWhere"
                :update-available-scenarios="replaceScenarioEveryWhere"
                :allow-incompatible="scenario.level < 2"
                :enable-resolved-check-mark="false"
                v-on="$listeners"
              />
            </td>
          </tr>
        </template>
      </v-data-table>
    </div>

    <footer-navigation
        :prev-button-function="backToIdentifyIncompatibleScenarii"
        prev-button-label="scenario.identifyIncompatibleScenarii"
        :next-button-function="goToResolveConflictWithMinimalScenarios"
        next-button-label="project.resolveConflictsWithinMinimumScenarios"
    />

    <delete-option-dialog
      :open="openDeleteDialog"
      :option="optionToDelete"
      @cancel-option-deletion="openDeleteDialog = false"
      @option-deleted="onOptionDeleted"
    />

    <edit-option-dialog
      :open="openEditOptionDialog"
      :confirm-edit="confirmEditOption"
      :option="optionToUpdate"
      @cancel-option-edition="openEditOptionDialog = false"
    />

    <edit-element-dialog
      :open.sync="openEditElementDialog"
      :element="elementToUpdate"
      @element-updated="replaceElementEveryWhere($event);"
      @cancel-element-edition="openEditElementDialog = false"
      v-on="$listeners"
    />
    
  </div>
</template>

<script>
import PageContentToolbar from '@/components/layout/PageContentToolbar';
import ScenarioBlock from '@/components/global/ScenarioBlock';
import OptionChip from '@/components/global/OptionChip';
import DeleteOptionDialog from '@/components/global/DeleteOptionDialog';
import EditOptionDialog from '@/components/global/EditOptionDialog';
import EditElementDialog from '@/components/global/EditElementDialog';
import ElementChip from '@/components/global/ElementChip';
import FooterNavigation from '@/components/layout/FooterNavigation';

import projectPageMixin from '@/mixins/project-page-mixin.js';

import optionService from '@/services/options.js';
import scenarioService from '@/services/scenarios.js';
import elementService from '@/services/elements.js';
import list from '@/utils/list.js';

export default {
  name: 'ScenariiList',

  components: { PageContentToolbar, ScenarioBlock, OptionChip, DeleteOptionDialog, EditOptionDialog , EditElementDialog , ElementChip, FooterNavigation },

  mixins: [ projectPageMixin ],

  data () {
    return {
      projectId: this.$route.params.id,
      options: [],
      availableElements: [],
      loadingOptions: false,
      openDeleteDialog: false,
      optionToDelete: {},
      openEditOptionDialog: false,
      optionToUpdate: {},
      openEditElementDialog: false,
      elementToUpdate: {},
      isLoading: false,
      preferences: [],
    };
  },

  computed: {
    optionHeaders () {
      return [{ text: this.$i18n.t('global.option'), value: 'option', width: '15%', sortable: false, align: 'center' },
        { text: this.$i18n.t('scenario.availableScenarios'), value: 'elements', width: '20%',  sortable: false, align: 'center' },
        { text: this.$i18n.t('option.contradictory'), value: 'contradictory', width: '20%',  sortable: false, align: 'center' },
        { text: '', value: 'actions', width: '5%',  sortable: false }
      ];
    },

    beliefHeaders () {
      return [{ text: this.$i18n.t('scenario.belief'), value: 'option', width: '15%', sortable: false, align: 'center' },
        { text: this.$i18n.t('scenario.availableScenarios'), value: 'elements', width: '20%',  sortable: false, align: 'center' },
        { text: this.$i18n.t('scenario.conflictingBelieves'), value: 'contradictory', width: '20%',  sortable: false, align: 'center' },
        { text: '', value: 'actions', width: '5%',  sortable: false }
      ];
    },

    optionsOnly() {
      return this.options.filter(opt => !opt.type);
    },

    beliefsOnly() {
      return this.options.filter(opt => opt?.type === 'belief');
    },

    showLogicalLanguage () {
      return this.$store.state.showLogicalLangage;
    },
  },

  methods: {
    editOption (option) {
      this.optionToUpdate = optionService.copy(option);
      this.openEditOptionDialog = true;
    },

    confirmEditOption () {
      //keep elements of this option
      let scenarios = this.optionToUpdate.scenarios;

      //prepare object for upadte request
      delete this.optionToUpdate.scenarios;

      optionService.updateOption(this.projectId, this.optionToUpdate).then(res => {
        let updatedOption = res.data;
        //restore option element
        updatedOption.scenarios = scenarios;
        let index = this.options.findIndex(option => option.id === updatedOption.id);
        updatedOption = Object.assign({}, this.options[index], updatedOption);

        this.options.forEach(option => {
          option.conflictingOptions
            .forEach((conflitctingOption, idx) => {
              if(conflitctingOption.id === updatedOption.id) {
                conflitctingOption = Object.assign({}, conflitctingOption, updatedOption);
                option.conflictingOptions.splice(idx, 1, conflitctingOption);
              }
            });
        });

        this.options.splice(this.options.findIndex(option => option.id === updatedOption.id), 1, updatedOption);
      }).catch(err => {
        this.$emit('raison-event', { type: 'error', messages: [err] });
      }).finally(() => {
        this.openEditOptionDialog = false;
        this.optionToUpdate = {};
      });
    },

    deleteOption (option) {
      this.optionToDelete = option;
      this.openDeleteDialog = true;
    },

    onOptionDeleted(optionDeletedEvent) {
      const deletedOptionId = optionDeletedEvent.optionId;
      this.options.splice(this.options.findIndex(option => option.id === deletedOptionId), 1);
      this.$emit('raison-event', { type: 'success', messages: [this.$t('option.successDeletion', {optionName: this.optionToDelete.name})] });

      this.openDeleteDialog = false;
      this.optionToDelete = {};
    },

    editElement (element) {
      this.elementToUpdate = JSON.parse(JSON.stringify(element));
      this.openEditElementDialog = true;
    },

    replaceElementEveryWhere (element) {
      this.isLoading = true;
      if(element.decisionPolicyUpdated) {
        this.init();
      }
      else {

        // copy object
        const optionsTemp = this.options.map(option => optionService.copy(option));

        optionsTemp.forEach(optionItem => {
          if(element.type === 'belief' && optionItem.id === element.id) {
            Object.assign(optionItem, element);
          }
          if (!optionItem.scenarios) {
            return;
          }
          optionItem.scenarios.forEach(scenarioItem => {
            scenarioItem.elements.forEach((elementItem, index) => {
              if ( elementItem.id === element.id ) {
                scenarioItem.elements.splice(index, 1, element);
              }
            });
          });
        });

        //replace element also in available elements list.
        this.$set(this.availableElements, this.availableElements.findIndex(elementItem => element.id === elementItem.id), element);

        this.options = optionsTemp;
      }
      this.isLoading = false;
    },

    replaceScenarioEveryWhere (scenario) {
      let optionsTemp = JSON.parse(JSON.stringify(this.options));

      optionsTemp.forEach(optionItem => {
        if (!optionItem.scenarios) {
          return;
        }
        optionItem.scenarios.forEach((scenarioItem, index) => {
          if (scenarioItem.id === scenario.id) {
            optionItem.scenarios.splice(index, 1, scenario);
          }
        });
      });

      this.options = optionsTemp;
    },

    deleteElement(option, scenario, element) {
      if(option?.type){
        elementService.deleteElement(this.projectId,option.id).then(() => {
          this.options.splice(this.options.findIndex(opt => opt.id === option.id), 1);
          const optionName = this.showLogicalLanguage ? option.name : option.prolog;
          this.$emit('raison-event', { type: 'success', messages: [this.$t('scenario.elementDeleted', {element: optionName})] });
        }).catch(err => {
          this.$emit('raison-event', { type: 'error', messages: [err] });
        });
      }
      else {
        const optionIndex = this.options.findIndex(optionItem => option.id === optionItem.id);
        let scenarioIndex = this.options[optionIndex].scenarios.findIndex(scenarioItem => scenario.id === scenarioItem.id);

        let copyScenario = JSON.parse(JSON.stringify(scenario));

        this.removeElementOfScenario(copyScenario, element);

        scenarioService.updateScenario(this.projectId, copyScenario).then(res => {
          let optionTemp = JSON.parse(JSON.stringify(this.options[optionIndex]));
          optionTemp.scenarios.splice(scenarioIndex, 1, res.data);
          this.options.splice(optionIndex, 1, optionTemp);
        }).catch(err => {
          this.$emit('raison-event', { type: 'error', messages: [err] });
        });
      }
     
    },

    removeElementOfScenario(scenario, element) {
      scenario.elements.splice(scenario.elements.findIndex(elementItem => elementItem.id === element.id), 1);
    },

    emitEvent (eventType, eventName, eventContent) {
      this.$emit(eventType, eventName, eventContent);
    },

    containsSamePreferenceOrSameScenario(preferences, preference) {
      return preferences.some(pref => pref.id === preference.id || pref.scenario.id === preference.scenario.id)
    },

    containsSameElements(scenario1, scenario2) {
      return list.sameById(scenario1.elements, scenario2.elements);
    },

    containsSameWigo(preference1, preference2) {
      return list.sameById(preference1.wigo, preference2.wigo);
    },

    removePreferencesWithTheSameScenarioAtGreaterLevel(preferences) {
      preferences.forEach(preference => {
        preference.scenario.hide = preferences
          .filter(pref => pref.id !== preference.id) // remove itself
          .some(
            pref => {
              if(pref.scenario.level > preference.scenario.level) {
                return this.containsSameElements(pref.scenario, preference.scenario);
              }
              else if(pref.scenario.level === preference.scenario.level 
                  && !pref.scenario.hide 
                  && this.containsSameElements(pref.scenario, preference.scenario)) {
                return this.containsSameWigo(pref, preference);
              }
              return false;
            }
          )
      });
    },

    buildOptionsWithPreferencesTree(options, preferences) {
      this.removePreferencesWithTheSameScenarioAtGreaterLevel(preferences);
      this.options.splice(0, this.options.length, ...options);
      // Group scenario preferences by option
      preferences.forEach(preference => {
        preference.wigo.forEach(wigo => {
          let option = this.options.find(opt => opt.id === wigo.id);
          if(!option.wigoPreferences) {
            option.wigoPreferences = [];
          }
          // Filter duplicates
          if(!preference.scenario.hide && !this.containsSamePreferenceOrSameScenario(option.wigoPreferences, preference)) {
            option.wigoPreferences.push(preference);
          }
        })
      });

      //group preferences by belief 
      preferences.forEach(preference => {
       preference.wigoBelief.forEach(wigo => {
         let option = this.options.filter(opt => opt.id === wigo.id)[0];
         if(!option) {
           option = wigo;
           this.options.push(option);
         }
         if(!option.wigoPreferences) {
           option.wigoPreferences = [];
         }
         // Filter duplicates
         if(!preference.scenario.hide && !this.containsSamePreferenceOrSameScenario(option.wigoPreferences, preference)) {
           option.wigoPreferences.push(preference);
         }
       })
     });
      
      this.options.forEach(opt => {
        if(opt.wigoPreferences && opt.wigoPreferences.length > 0) {
          opt.scenarios = opt.wigoPreferences
            .map(pref => pref.scenario)
            .filter(scenario => !scenario.hide || opt?.type === 'belief')
            .sort((s1, s2) => s1.level - s2.level);
        }
        opt.wigoPreferences = null;
      });
     },

    dispathConflictingOptions(conflicts) {
      this.options.forEach(option => {
        option.conflictingOptions = conflicts
          .filter(conflict => conflict.option.id === option.id)
          .map(conflict => conflict.optioncomplement);
        
        option.conflictingOptions = [
          ...option.conflictingOptions,
          ...conflicts
            .filter(conflict => conflict.optioncomplement.id === option.id)
            .map(conflict => conflict.option)
        ];
      });
    },

    backToIdentifyIncompatibleScenarii () {
      this.$router.push({name: 'incompatibleScenarii', params: { id: this.$route.params.id }});
    },

    goToResolveConflictWithMinimalScenarios () {
      this.$router.push({name: 'conflictResolution', params: { id: this.$route.params.id } });
    },

    init() {
      this.isLoading = true;
      Promise.all([
        scenarioService.getAllPreferences(this.projectId),
        optionService.getAllConflictingOptions(this.projectId),
        elementService.getElements(this.projectId),
        optionService.getOptions(this.projectId),
      ])
      .then(responses => {
        this.buildOptionsWithPreferencesTree(responses[3].data, responses[0].data);
        this.dispathConflictingOptions(responses[1].data);
        this.availableElements.splice(0, this.availableElements, responses[2].data);
      })
      .catch(err => {
        console.log(err);
        this.$emit('raison-event', { type: 'error', messages: [err] });
      })
      .finally(() => {
        this.isLoading = false;
      });
    }
  },

  created () {
    this.init();
  },
};
</script>

<style>
.rai-table__column {
  text-align: center;
}
</style>